import mitt from 'mitt'
import Modal from './Modal.vue'
import Dialog from './Dialog.vue'
import ModalsContainer from './ModalsContainer.vue'
import {
    createDivInBody
} from './utils'
import { createApp, h } from 'vue'

const defaultComponentName = 'Modal'

const UNMOUNTED_ROOT_ERROR_MESSAGE =
    '[vue-js-modal] ' +
    'In order to render dynamic modals, a <modals-container> ' +
    'component must be present on the page.'

const DYNAMIC_MODAL_DISABLED_ERROR =
    '[vue-js-modal] ' +
    '$modal() received object as a first argument, but dynamic modals are ' +
    'switched off. https://github.com/euvl/vue-js-modal/#dynamic-modals'

const UNSUPPORTED_ARGUMENT_ERROR =
    '[vue-js-modal] ' +
    '$modal() received an unsupported argument as a first argument.'

export const getModalsContainer = (app, options, root) => {
    if (!root._dynamicContainer && options.injectModalsContainer) {
        const container = createDivInBody()
        const modalApp = createApp({
            render() { return h(ModalsContainer) }
        })
        modalApp.mount(container)
    }

    return root._dynamicContainer
}

const Plugin = {
    install(app, options = {}) {
        /**
         * Makes sure that plugin can be installed only once
         */
        if (this.installed) {
            return
        }

        this.installed = true
        this.event = mitt()
        this.rootInstance = null

        const componentName = options.componentName || defaultComponentName
        const dynamicDefaults = options.dynamicDefaults || {}
        /**
         * Plugin API
         */
        const showStaticModal = (modal, params) => {
            Plugin.event.emit('toggle', { name: modal, state: true, params })
        }

        const showDynamicModal = (modal, props, params, events) => {
            const root = params && params.root ? params.root : Plugin.rootInstance
            const container = getModalsContainer(app, options, root)
            if (container) {
                container.add(modal, props, {
                    ...dynamicDefaults,
                    ...params
                }, events)
                return
            }

            console.warn(UNMOUNTED_ROOT_ERROR_MESSAGE)
        }
      const IGNORE_HISTORY_MODALS = new Set(['dialog'])
 const ModalManager = {
  _stack: [],
  _queue: Promise.resolve(),
  _handlingPop: false,


   _run(fn) {
  this._queue = this._queue.then(() => {
    return Promise.resolve().then(fn)
  })
  return this._queue
},

  show(name, ...args) {
    return this._run(() => {
    const ignoreHistory = IGNORE_HISTORY_MODALS.has(name)
      if (!ignoreHistory) {
      this._stack = this._stack.filter(e => e.name !== name)

      const ts = Date.now()

      history.pushState({ ...history.state, overlay: name, type: 'modal', ts }, '')

      this._stack.push({
        type: 'modal',
        name
      })
      }
      showStaticModal(name, ...args)
    })
  },

  hide(name, params) {
      const stack = this._stack
      const top = this._stack[this._stack.length - 1]

      // Always remove from stack and emit close immediately.
      // If this modal was pushed via history.pushState, also call
      // history.back() — but do so AFTER cleanup so the async popstate
      // handler (closeTop) doesn't accidentally close whatever is next
      // on the stack (e.g. a drawer that opened in the meantime).
      this._stack = stack.filter(entry => entry.name !== name)
      Plugin.event.emit('toggle', { name, state: false, params })

      if (!this._handlingPop && top && top.name === name) {
        this._handlingPop = true
        history.back()
        setTimeout(() => { this._handlingPop = false }, 50)
      }
  },

  toggle(name, params) {
    Plugin.event.emit('toggle', { name, state: undefined, params })
  },

  register(type, name, closeFn) {
    return this._run(() => {
      const top = this._stack[this._stack.length - 1]
      if (top && top.name === name) return

      const ts = Date.now()

      history.pushState({ ...history.state, overlay: name, type, ts }, '')

      this._stack.push({
        type,
        name,
        closeFn
      })
    })
  },

  closeTop() {
    const entry = this._stack.pop()
    if (!entry) return

    if (entry.type === 'modal') {
      Plugin.event.emit('toggle', { name: entry.name, state: false })
    } else if (entry.closeFn) {
      entry.closeFn()
    }
  }
}

app.config.globalProperties.$modal = ModalManager

        /**
         * Sets custom component name (if provided)
         */
        app.component(componentName, Modal)
        /**
         * Registration of <Dialog/> component
         */
        if (options.dialog) {
            app.component('VDialog', Dialog)
        }
        /**
         * Registration of <ModalsContainer/> component
         */
        if (options.dynamic) {
            app.component('ModalsContainer', ModalsContainer)
            app.mixin({
                beforeMount() {
                    if (Plugin.rootInstance === null) {
                        Plugin.rootInstance = this.$root
                    }
                }
            })
        }
    }
}


export default Plugin
